import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useContentPayload } from '../useContentPayload';
import { useCreateContentMutation } from '../useCreateContentMutation';
import { usePublishContentMutation } from '../usePublishContentMutation';
import { useUpdateContentMutation } from '../useUpdateContentMutation';
import { updateTemporaryId } from 'src/core/services/upload/utils';
import { Toast } from 'src/shared/components/toast';
import dispatchSubmitDatahubEvents from './dispatchSubmitDatahubEvents';
export const useSubmitMutations = ({ isUpdate, onSuccess, isCreateAnotherContentEnabled }) => {
    const { t } = useTranslation();
    const { moduleId, pageId } = useParams();
    const [contentId, setContentId] = useState(null);
    const { mapContentFormValuesToPayload } = useContentPayload(isUpdate, pageId);
    const createContentMutation = useCreateContentMutation();
    const publishContentMutation = usePublishContentMutation();
    const updateContentMutation = useUpdateContentMutation(pageId);
    const handleSave = (formData) => {
        const payload = mapContentFormValuesToPayload(moduleId, formData);
        const submitMutation = isUpdate ? updateContentMutation : createContentMutation;
        submitMutation.mutate(payload, {
            onSuccess: (response) => {
                onSuccess({
                    payload,
                    pageId: response.data.id || pageId,
                    isPublished: formData.published ?? false
                });
                if (!isUpdate) {
                    Toast.show({ type: 'success', message: t('content.toast.create.sucess') });
                    response.data.id && updateTemporaryId(response.data.id);
                }
            },
            onError: () => {
                !isUpdate && Toast.show({ type: 'danger', message: t('content.toast.create.error') });
            },
            onSettled: (response, error) => {
                dispatchSubmitDatahubEvents({
                    error,
                    payload,
                    response,
                    action: isUpdate ? 'update' : 'create',
                    createNextEnable: isCreateAnotherContentEnabled,
                    pageId: pageId === 'new' ? response?.data.id : pageId
                });
            }
        });
    };
    function handlePublish(contentId, payload) {
        const publishPayload = {
            modules: null,
            published: true,
            notifyEmail: false,
            pages: [contentId]
        };
        publishContentMutation.mutate(publishPayload, {
            onSuccess: () => {
                Toast.show({ type: 'success', message: t('content.toast.publish.sucess') });
                contentId && updateTemporaryId(contentId);
                onSuccess({ payload, isPublished: true, pageId: contentId || pageId });
                setContentId(null);
            },
            onError: () => {
                Toast.show({ type: 'danger', message: t('content.toast.publish.just_created') });
            },
            onSettled: (response, error) => {
                dispatchSubmitDatahubEvents({
                    error,
                    payload,
                    response,
                    action: 'publish',
                    pageId: contentId,
                    createNextEnable: isCreateAnotherContentEnabled
                });
            }
        });
    }
    function handleCreateAndPublish(formData) {
        const payload = mapContentFormValuesToPayload(moduleId, formData);
        if (!contentId) {
            createContentMutation.mutate(payload, {
                onSuccess: (response) => {
                    setContentId(response.data.id);
                    handlePublish(response.data.id, payload);
                },
                onError: () => {
                    Toast.show({ type: 'danger', message: t('content.toast.publish.error') });
                },
                onSettled: (response, error) => {
                    dispatchSubmitDatahubEvents({
                        error,
                        payload,
                        response,
                        action: 'create',
                        pageId: response?.data.id ?? '',
                        createNextEnable: isCreateAnotherContentEnabled
                    });
                }
            });
            return;
        }
        handlePublish(contentId, payload);
    }
    return {
        handleSave,
        handlePublish,
        handleCreateAndPublish,
        isSubmitting: createContentMutation.isPending ||
            publishContentMutation.isPending ||
            updateContentMutation.isPending
    };
};
